<template>
  <div
    class="upload"
    :class="{ 'upload--error': isInvalid }"
  >
    <file-pond
      class="upload__pond"
      allow-multiple
      :accepted-file-types="ACCEPTED_FILE_TYPES"
      :disabled="$props.disabled"
      :invalid="isInvalid"
      :max-files="MAX_FILES"
      :max-file-size="MAX_FILE_SIZE"
      :number-of-existing-files="files.length"
      :supporting-text="$props.supportingText"
      @error="handleError"
      @upload="handleUpload"
    />

    <div class="upload__hint caption-regular">
      <span v-text="autoHint" />
    </div>

    <div
      v-if="$props.message"
      class="upload__message body-2-compressed"
      v-text="$props.message"
    />

    <basic-list
      button-icon="trash-can-xmark"
      :button-label="$t('screen.personalData.documents.remove')"
      :entries="files"
      @button-click="handleDelete"
    />
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, ref } from 'vue'

  import { loading$ } from '@shared/services'

  import documentUploadAPI from '@dvp/api/documentUpload'
  import useI18n from '@shared/hooks/useI18n'
  import useSnackbar from '@shared/hooks/useSnackbar'

  import FilePond from '@shared/components/Basic/File/FilePond'
  import BasicList from '@shared/components/Basic/List'

  import { NOTIFICATION } from '@shared/config/constants'
  import useVuelidate from '@vuelidate/core'
  import { requiredIf } from '@vuelidate/validators'

  // HOOKS
  const { t } = useI18n()
  const { addNotification } = useSnackbar()

  // INIT
  const ACCEPTED_FILE_TYPES = 'image/jpeg,image/png,application/pdf'
  const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
  const MAX_FILES = 4

  const props = defineProps({
    basketId: {
      type: String,
      required: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    documentType: {
      type: String,
      required: true,
    },

    isRequired: {
      type: Boolean,
      default: false,
    },

    message: {
      type: String,
      default: undefined,
    },

    personId: {
      type: String,
      default: null,
    },

    supportingText: {
      type: String,
      default: undefined,
    },
  })

  // DATA
  const files = ref([])

  // COMPUTED
  const autoHint = computed(() => {
    if (isInvalid.value) {
      const error = v.value.$errors[0]?.$validator
      if (error) return t(`form.error.${error}`)
    }
    return props.supportingText
  })

  const isInvalid = computed(() => {
    return v.value.$dirty && v.value.$invalid
  })

  // METHODS
  /*
async function getFile(fileId) {
  return await documentUploadAPI.getDocument({
    basketId: props.basketId,
    personId: props.personId,
    documentId: fileId
  })
}
*/
  async function deleteFile(fileId) {
    return await documentUploadAPI.deleteDocument({
      basketId: props.basketId,
      personId: props.personId,
      documentId: fileId,
    })
  }

  async function loadFiles() {
    const allFiles = await documentUploadAPI.getDocuments({
      basketId: props.basketId,
      personId: props.personId,
    })

    return allFiles.filter(file => file.type === props.documentType)
  }

  async function uploadFiles(newFiles) {
    const uploadAllFiles = newFiles.map(newFile => {
      return documentUploadAPI.uploadDocument({
        basketId: props.basketId,
        personId: props.personId,
        type: props.documentType,
        file: newFile,
      })
    })

    const result = await Promise.allSettled(uploadAllFiles)
    const successfulUploads = result.filter(r => r.status === 'fulfilled').length

    if (successfulUploads === 0) {
      return Promise.reject('No files uploaded')
    }

    return successfulUploads
  }

  async function handleDelete(fileIndex) {
    try {
      loading$.start()
      const file = files.value[fileIndex]
      await deleteFile(file.id)
      files.value = await loadFiles()
      loading$.end()
    } catch {
      loading$.failed()
    }
  }

  async function handleUpload(newFiles) {
    try {
      loading$.start()

      const successfulUploads = await uploadFiles(newFiles)
      addNotification({
        text: t('form.file.uploaded', { count: successfulUploads }),
      })

      files.value = await loadFiles()

      loading$.end()
    } catch {
      loading$.failed()
    }
  }

  function handleError(error) {
    addNotification({
      text: Array.isArray(error.messages) ? error.messages[0] : t(`error.${error}`),
      type: NOTIFICATION.ERROR,
    })
  }

  // LIFECYCLE HOOKS
  onBeforeMount(async () => {
    files.value = await loadFiles()
  })

  // VALIDATION
  const v = useVuelidate({ missingFiles: requiredIf(props.isRequired) }, files)
</script>

<style scoped>
.upload {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-02);
}

.upload__hint {
  margin: calc(var(--fixed-spacing-fix-01) * -1) 0 0 var(--fixed-spacing-fix-04);
}

.upload__message {
  padding: var(--fixed-spacing-fix-04);
  border-radius: var(--fixed-border-radius-fix-02);
  background-color: var(--surface-ext-a);
}

.upload--error .upload__hint {
  color: var(--error);
}

.upload__content {
  display: flex;
  justify-content: center;
  align-items: center;
}

.upload__icon {
  margin-right: var(--fixed-spacing-fix-02);
}
</style>
